import React, { useState, useEffect, useContext } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  LinearProgress,
  Tooltip
} from '@mui/material';
import { api } from 'common';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import moment from 'moment';
import {
  TrendingUp,
  People,
  AttachMoney,
  Stars,
  EmojiEvents,
  Groups,
  PersonAdd
} from '@mui/icons-material';
import { MAIN_COLOR, SECONDORY_COLOR } from '../common/sharedFunctions';

// Tier configurations
const TIER_CONFIGS = {
  brown: { name: 'ALPHA', color: '#8B4513', minContribution: 200 },
  gold: { name: 'BETA', color: '#FFD700', minContribution: 300 },
  diamond: { name: 'GAMMA', color: '#B9F2FF', minContribution: 400 },
  platinum: { name: 'DELTA', color: '#E5E4E2', minContribution: 500 }
};

export default function AgentAnalytics() {
  const { t } = useTranslation();
  const settings = useSelector((state) => state.settingsdata?.settings);
  const staticusers = useSelector((state) => state.usersdata?.staticusers);
  const auth = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30'); // days
  const [country, setCountry] = useState('all');
  
  // Analytics data
  const [agentStats, setAgentStats] = useState({
    totalAgents: 0,
    activeAgents: 0,
    totalPartners: 0,
    totalEarnings: 0,
    avgPartnersPerAgent: 0,
    avgEarningsPerAgent: 0
  });
  const [tierDistribution, setTierDistribution] = useState({
    brown: 0,
    gold: 0,
    diamond: 0,
    platinum: 0,
    inactive: 0
  });
  const [topAgents, setTopAgents] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [growthData, setGrowthData] = useState([]);
  const [countryDistribution, setCountryDistribution] = useState([]);
  
  // Get unique countries from users
  const countries = React.useMemo(() => {
    if (!staticusers) return [];
    const countrySet = new Set();
    staticusers.forEach(user => {
      if (user.country) countrySet.add(user.country.toLowerCase());
    });
    return Array.from(countrySet).sort();
  }, [staticusers]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [staticusers, timeRange, country]);
  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const { database } = api;
      
      // Filter agents based on country
      let agents = staticusers?.filter(user => 
        user.usertype?.includes('agent') &&
        (country === 'all' || user.country?.toLowerCase() === country)
      ) || [];
      
      // Fetch agent rewards data
      const rewardsSnapshot = await database().ref('agentRewards').once('value');
      const rewardsData = rewardsSnapshot.val() || {};
      
      // Calculate tier distribution
      const tiers = { brown: 0, gold: 0, diamond: 0, platinum: 0, inactive: 0 };
      let totalPartners = 0;
      let totalEarnings = 0;
      let activeAgents = 0;
      const agentsWithData = agents.map(agent => {
        const activation = parseFloat(agent.agentActivationPaid || 0);
        const rewards = rewardsData[agent.uid] || {};
        const partnersCount = rewards.partners ? Object.keys(rewards.partners).length : 0;
        const earnings = parseFloat(rewards.totalEarnings || 0);
        
        // Determine tier
        let tier = 'inactive';
        if (activation >= 500) tier = 'platinum';
        else if (activation >= 400) tier = 'diamond';
        else if (activation >= 300) tier = 'gold';
        else if (activation >= 200) tier = 'brown';
        
        tiers[tier]++;
        
        if (tier !== 'inactive') {
          activeAgents++;
          totalPartners += partnersCount;
          totalEarnings += earnings;
        }
        
        return {
          ...agent,
          tier,
          partnersCount,
          earnings,
          activation
        };
      });
      
      // Set tier distribution
      setTierDistribution(tiers);
      
      // Calculate stats
      setAgentStats({
        totalAgents: agents.length,
        activeAgents,
        totalPartners,
        totalEarnings,
        avgPartnersPerAgent: activeAgents > 0 ? (totalPartners / activeAgents).toFixed(1) : 0,
        avgEarningsPerAgent: activeAgents > 0 ? (totalEarnings / activeAgents).toFixed(2) : 0
      });
      
      // Top agents by partners
      const sorted = agentsWithData
        .filter(a => a.tier !== 'inactive')
        .sort((a, b) => b.partnersCount - a.partnersCount)
        .slice(0, 10);
      setTopAgents(sorted);
      
      // Country distribution
      const countryStats = {};
      agentsWithData.forEach(agent => {
        const c = agent.country || 'Unknown';
        if (!countryStats[c]) {
          countryStats[c] = { count: 0, active: 0, partners: 0, earnings: 0 };
        }
        countryStats[c].count++;
        if (agent.tier !== 'inactive') {
          countryStats[c].active++;
          countryStats[c].partners += agent.partnersCount;
          countryStats[c].earnings += agent.earnings;
        }
      });
      
      setCountryDistribution(
        Object.entries(countryStats)
          .map(([name, data]) => ({ name, ...data }))
          .sort((a, b) => b.count - a.count)
      );
      
      // Growth data (mock for now - would need historical data)
      const days = parseInt(timeRange);
      const growth = [];
      for (let i = days; i >= 0; i--) {
        const date = moment().subtract(i, 'days').format('MMM DD');
        // Simulate growth trend based on current data
        const baseAgents = Math.max(0, agents.length - Math.floor(Math.random() * 5 * (i / days)));
        growth.push({
          date,
          agents: baseAgents,
          partners: Math.floor(totalPartners * (1 - i / days / 2))
        });
      }
      setGrowthData(growth);
      
      // Recent activity from agent rewards logs
      const logsSnapshot = await database().ref('agentRewardsLogs')
        .orderByChild('timestamp')
        .limitToLast(10)
        .once('value');
      const logsData = logsSnapshot.val() || {};
      const activities = Object.entries(logsData)
        .filter(([key]) => key !== 'latest')
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 5);
      setRecentActivity(activities);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setLoading(false);
    }
  };

  // Chart configurations
  const tierChartOptions = {
    chart: { type: 'donut' },
    labels: ['ALPHA (Brown)', 'BETA (Gold)', 'GAMMA (Diamond)', 'DELTA (Platinum)', 'Inactive'],
    colors: ['#8B4513', '#FFD700', '#00BCD4', '#9E9E9E', '#CCCCCC'],
    legend: { position: 'bottom' },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total Agents',
              formatter: () => agentStats.totalAgents
            }
          }
        }
      }
    }
  };
  const tierChartSeries = [
    tierDistribution.brown,
    tierDistribution.gold,
    tierDistribution.diamond,
    tierDistribution.platinum,
    tierDistribution.inactive
  ];
  const growthChartOptions = {
    chart: { type: 'area', toolbar: { show: false } },
    xaxis: { categories: growthData.map(d => d.date) },
    colors: [MAIN_COLOR, '#4CAF50'],
    stroke: { curve: 'smooth', width: 2 },
    fill: { type: 'gradient', gradient: { opacityFrom: 0.6, opacityTo: 0.1 } },
    legend: { position: 'top' },
    tooltip: { shared: true }
  };
  const growthChartSeries = [
    { name: 'Agents', data: growthData.map(d => d.agents) },
    { name: 'Partners', data: growthData.map(d => d.partners) }
  ];
  const getTierChip = (tier) => {
    const config = TIER_CONFIGS[tier];
    if (!config) return <Chip label="Inactive" size="small" />;
    return (
      <Chip 
        label={config.name} 
        size="small" 
        sx={{ 
          bgcolor: config.color, 
          color: tier === 'gold' || tier === 'platinum' ? '#000' : '#fff',
          fontWeight: 'bold'
        }} 
      />
    );
  };
  const StatCard = ({ title, value, icon, subtitle, color }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={color || 'primary'}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ 
            bgcolor: `${color || MAIN_COLOR}20`, 
            borderRadius: 2, 
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          {t('agent_analytics') || 'Agent Analytics'}
        </Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('time_range') || 'Time Range'}</InputLabel>
            <Select
              value={timeRange}
              label={t('time_range') || 'Time Range'}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="7">7 {t('days') || 'Days'}</MenuItem>
              <MenuItem value="30">30 {t('days') || 'Days'}</MenuItem>
              <MenuItem value="90">90 {t('days') || 'Days'}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('country') || 'Country'}</InputLabel>
            <Select
              value={country}
              label={t('country') || 'Country'}
              onChange={(e) => setCountry(e.target.value)}
            >
              <MenuItem value="all">{t('all_countries') || 'All Countries'}</MenuItem>
              {countries.map(c => (
                <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title={t('total_agents') || 'Total Agents'}
            value={agentStats.totalAgents}
            icon={<People sx={{ fontSize: 32, color: MAIN_COLOR }} />}
            color={MAIN_COLOR}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title={t('active_agents') || 'Active Agents'}
            value={agentStats.activeAgents}
            icon={<Stars sx={{ fontSize: 32, color: '#4CAF50' }} />}
            subtitle={`${((agentStats.activeAgents / agentStats.totalAgents) * 100 || 0).toFixed(0)}% ${t('activation_rate') || 'activation rate'}`}
            color="#4CAF50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title={t('total_partners') || 'Total Partners'}
            value={agentStats.totalPartners}
            icon={<Groups sx={{ fontSize: 32, color: '#2196F3' }} />}
            color="#2196F3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title={t('total_earnings') || 'Total Earnings'}
            value={`${settings?.symbol || '$'}${agentStats.totalEarnings.toFixed(2)}`}
            icon={<AttachMoney sx={{ fontSize: 32, color: '#FF9800' }} />}
            color="#FF9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title={t('avg_partners') || 'Avg Partners/Agent'}
            value={agentStats.avgPartnersPerAgent}
            icon={<PersonAdd sx={{ fontSize: 32, color: '#9C27B0' }} />}
            color="#9C27B0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title={t('avg_earnings') || 'Avg Earnings/Agent'}
            value={`${settings?.symbol || '$'}${agentStats.avgEarningsPerAgent}`}
            icon={<TrendingUp sx={{ fontSize: 32, color: '#00BCD4' }} />}
            color="#00BCD4"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} mb={3}>
        {/* Tier Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('tier_distribution') || 'Tier Distribution'}
              </Typography>
              <Chart
                options={tierChartOptions}
                series={tierChartSeries}
                type="donut"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Growth Chart */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('growth_trend') || 'Growth Trend'}
              </Typography>
              <Chart
                options={growthChartOptions}
                series={growthChartSeries}
                type="area"
                height={280}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tables Row */}
      <Grid container spacing={3}>
        {/* Top Agents */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <EmojiEvents sx={{ color: '#FFD700' }} />
                <Typography variant="h6">
                  {t('top_agents') || 'Top Performing Agents'}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>{t('name') || 'Name'}</TableCell>
                      <TableCell>{t('tier') || 'Tier'}</TableCell>
                      <TableCell align="right">{t('partners') || 'Partners'}</TableCell>
                      <TableCell align="right">{t('earnings') || 'Earnings'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topAgents.map((agent, index) => (
                      <TableRow key={agent.uid}>
                        <TableCell>
                          {index < 3 ? (
                            <EmojiEvents sx={{ 
                              color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32',
                              fontSize: 20 
                            }} />
                          ) : (
                            index + 1
                          )}
                        </TableCell>
                        <TableCell>
                          {agent.firstName} {agent.lastName}
                        </TableCell>
                        <TableCell>{getTierChip(agent.tier)}</TableCell>
                        <TableCell align="right">{agent.partnersCount}</TableCell>
                        <TableCell align="right">
                          {settings?.symbol || '$'}{agent.earnings.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {topAgents.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          {t('no_data') || 'No data available'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Country Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('country_distribution') || 'Agents by Country'}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('country') || 'Country'}</TableCell>
                      <TableCell align="right">{t('total') || 'Total'}</TableCell>
                      <TableCell align="right">{t('active') || 'Active'}</TableCell>
                      <TableCell align="right">{t('partners') || 'Partners'}</TableCell>
                      <TableCell>{t('activation_rate') || 'Activation Rate'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {countryDistribution.slice(0, 8).map((c) => (
                      <TableRow key={c.name}>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{c.name}</TableCell>
                        <TableCell align="right">{c.count}</TableCell>
                        <TableCell align="right">{c.active}</TableCell>
                        <TableCell align="right">{c.partners}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LinearProgress 
                              variant="determinate" 
                              value={(c.active / c.count) * 100 || 0}
                              sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 35 }}>
                              {((c.active / c.count) * 100 || 0).toFixed(0)}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {countryDistribution.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          {t('no_data') || 'No data available'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
